/*
 * Copyright 2026 Sony Corporation
 */

import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import CropFreeIcon from '@mui/icons-material/CropFree';
import DeleteOutlinedIcon from '@mui/icons-material/DeleteOutlined';
import DownloadIcon from '@mui/icons-material/Download';
import NearMeOutlinedIcon from '@mui/icons-material/NearMeOutlined';
import OpenWithIcon from '@mui/icons-material/OpenWith';
import RedoIcon from '@mui/icons-material/Redo';
import RotateRightIcon from '@mui/icons-material/RotateRight';
import StraightenIcon from '@mui/icons-material/Straighten';
import UndoIcon from '@mui/icons-material/Undo';
import ZoomOutMapIcon from '@mui/icons-material/ZoomOutMap';
import { Divider, IconButton, Paper, Tooltip } from '@mui/material';

import type { GizmoMode } from '../../scene/node-gizmo';

const MODES: { id: GizmoMode; label: string; key: string; icon: React.ReactNode }[] = [
  { id: 'select', label: 'Select', key: 'Q', icon: <NearMeOutlinedIcon fontSize="small" /> },
  { id: 'move', label: 'Move', key: 'W', icon: <OpenWithIcon fontSize="small" /> },
  { id: 'rotate', label: 'Rotate', key: 'E', icon: <RotateRightIcon fontSize="small" /> },
  { id: 'scale', label: 'Scale', key: 'R', icon: <ZoomOutMapIcon fontSize="small" /> },
];

export interface ViewportActionsProps {
  mode: GizmoMode;
  onModeChange: (mode: GizmoMode) => void;
  hasSelection: boolean;
  onDuplicate: () => void;
  onDelete: () => void;
  /** The selection if there is one, the whole scene if there is not. */
  onFrame: () => void;
  /** Only a library asset has a scale of its own to set. */
  canMeasure: boolean;
  measuring: boolean;
  onMeasure: () => void;
  canUndo: boolean;
  canRedo: boolean;
  onUndo: () => void;
  onRedo: () => void;
  onSave: () => void;
}

/** A disabled button fires no events, and a tooltip needs one to know it is hovered. */
const Action = ({
  title,
  disabled,
  selected,
  onClick,
  children,
}: {
  title: string;
  disabled?: boolean;
  selected?: boolean;
  onClick: () => void;
  children: React.ReactNode;
}) => (
  <Tooltip title={title}>
    <span>
      <IconButton
        size="small"
        aria-label={title}
        aria-pressed={selected}
        disabled={disabled}
        onClick={onClick}
        sx={{ bgcolor: selected ? 'action.selected' : 'transparent', color: selected ? 'text.primary' : undefined }}
      >
        {children}
      </IconButton>
    </span>
  </Tooltip>
);

/**
 * Everything you can do to what is selected, along the bottom of the viewport.
 *
 * One bar, not a bar and a rail: the two carried the same commands, and a
 * second place to find them is a second place to keep consistent for no gain.
 *
 * Icons only, because labels made the bar wider than the viewport it floats
 * over. The name and the shortcut are in every tooltip, which is where anyone
 * learns a shortcut anyway.
 */
export const ViewportActions = ({
  mode,
  onModeChange,
  hasSelection,
  onDuplicate,
  onDelete,
  onFrame,
  canMeasure,
  measuring,
  onMeasure,
  canUndo,
  canRedo,
  onUndo,
  onRedo,
  onSave,
}: ViewportActionsProps) => (
  <Paper elevation={3} className="absolute bottom-4 left-1/2 flex -translate-x-1/2 items-center gap-0.5 p-1">
    {MODES.map((item) => (
      <Action
        key={item.id}
        title={`${item.label} (${item.key})`}
        selected={mode === item.id}
        onClick={() => onModeChange(item.id)}
      >
        {item.icon}
      </Action>
    ))}

    <Divider orientation="vertical" flexItem className="mx-1" />

    <Action title={hasSelection ? 'Frame selection (F)' : 'Frame scene (F)'} onClick={onFrame}>
      <CropFreeIcon fontSize="small" />
    </Action>
    <Action
      title={canMeasure ? 'Set scale from a known distance' : 'Set scale: select an asset from My Library'}
      disabled={!canMeasure && !measuring}
      selected={measuring}
      onClick={onMeasure}
    >
      <StraightenIcon fontSize="small" />
    </Action>

    <Divider orientation="vertical" flexItem className="mx-1" />

    <Action title="Duplicate" disabled={!hasSelection} onClick={onDuplicate}>
      <ContentCopyIcon fontSize="small" />
    </Action>
    <Action title="Delete (Del)" disabled={!hasSelection} onClick={onDelete}>
      <DeleteOutlinedIcon fontSize="small" />
    </Action>

    <Divider orientation="vertical" flexItem className="mx-1" />

    <Action title="Undo (Ctrl+Z)" disabled={!canUndo} onClick={onUndo}>
      <UndoIcon fontSize="small" />
    </Action>
    <Action title="Redo (Ctrl+Shift+Z)" disabled={!canRedo} onClick={onRedo}>
      <RedoIcon fontSize="small" />
    </Action>

    <Divider orientation="vertical" flexItem className="mx-1" />

    <Action title="Save" onClick={onSave}>
      <DownloadIcon fontSize="small" />
    </Action>
  </Paper>
);
